import { useMemo } from 'react';

import { computePosition, flip, shift } from '@floating-ui/dom';
import { Editor, posToDOMRect, Range, ReactRenderer } from '@tiptap/react';
import { SuggestionOptions } from '@tiptap/suggestion';

import { CommandList } from './CommandList';
import { insertSlashCommandChip } from './insertSlashCommand';
import { SuggestionItemGroup } from './SlashExtension.types';

let resizeObserver: ResizeObserver;
let intersectionObserver: IntersectionObserver;
let windowEventListener: () => void;

const updatePosition = (editor: Editor, element: HTMLElement) => {
  const doUpdate = (editor: Editor, element: HTMLElement) => {
    const suggestionStart = (editor.state as any).suggestion$.range.from;
    const virtualElement = {
      getBoundingClientRect: () =>
        posToDOMRect(editor.view, suggestionStart, suggestionStart),
    };

    computePosition(virtualElement, element, {
      placement: 'top-start',
      strategy: 'absolute',
      middleware: [shift(), flip()],
    }).then(({ x, y, strategy }) => {
      element.style.width = 'max-content';
      element.style.position = strategy;
      element.style.left = `${x}px`;
      element.style.top = `${y - 10}px`;
    });
  };

  if (resizeObserver) {
    resizeObserver.disconnect();
  }
  if (windowEventListener) {
    window.removeEventListener('resize', windowEventListener);
    window.removeEventListener('orientationchange', windowEventListener);
  }
  if (intersectionObserver) {
    intersectionObserver.disconnect();
  }

  if (editor.view.dom) {
    resizeObserver = new ResizeObserver(() => {
      try {
        if (editor.view.dom) {
          doUpdate(editor, element);
        } else {
          resizeObserver.disconnect();
        }
      } catch {
        resizeObserver.disconnect();
      }
    });

    windowEventListener = () => {
      doUpdate(editor, element);
    };

    window.addEventListener('resize', windowEventListener);
    window.addEventListener('orientationchange', windowEventListener);
    resizeObserver.observe(editor.view.dom);

    intersectionObserver = new IntersectionObserver(() => {
      doUpdate(editor, element);
    });
    intersectionObserver.observe(editor.view.dom);
  }
};

export function useSuggestions(suggestions: SuggestionItemGroup[]) {
  const refinedSuggestions = useMemo(() => {
    return suggestions.map(group => {
      return {
        label: group.label,
        icon: group.icon,
        highlight: group.highlight,
        command: ({ editor, range }: { editor: Editor; range: Range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertContent(`/ ${group.highlight} `)
            .run();
        },
        items: group.items.map(item => {
          return {
            label: item,
            icon: group.icon,
            highlight: item,
            command: ({ editor, range }: { editor: Editor; range: Range }) => {
              insertSlashCommandChip({
                editor,
                item,
                icon: group.icon,
                range,
              });
            },
          };
        }),
      };
    });
  }, [suggestions]);

  return useMemo<Partial<SuggestionOptions>>(() => {
    return {
      items: ({ query }: { query: string; editor: Editor }) => {
        let lowerCaseQuery = query.toLowerCase();

        // Trim only first leading space
        if (lowerCaseQuery.startsWith(' ')) {
          lowerCaseQuery = lowerCaseQuery.slice(1);
        }

        const matchingGroup = refinedSuggestions.find(group =>
          lowerCaseQuery.startsWith(group.highlight.toLowerCase())
        );

        if (matchingGroup) {
          return {
            items: matchingGroup.items.filter(item =>
              item.label.toLowerCase().includes(lowerCaseQuery)
            ),
            groupLabel: matchingGroup.label,
          } as any;
        }

        return {
          items: refinedSuggestions.filter(
            group =>
              group.label.toLowerCase().includes(lowerCaseQuery) ||
              group.highlight.toLowerCase().includes(lowerCaseQuery)
          ),
        } as any;
      },

      render: () => {
        let component: ReactRenderer;

        return {
          onStart: props => {
            component = new ReactRenderer(CommandList, {
              props,
              editor: props.editor,
            });

            if (!props.clientRect) {
              return;
            }

            component.element.style.position = 'absolute';
            component.element.style.zIndex = '1500';

            document.body.appendChild(component.element);

            updatePosition(props.editor, component.element);
          },

          onUpdate(props) {
            component.updateProps(props);

            if (!props.clientRect) {
              return;
            }

            updatePosition(props.editor, component.element);
          },

          onKeyDown(props) {
            if (props.event.key === 'Escape') {
              component.destroy();
              component.element.remove();

              return true;
            }

            return (component.ref as any)?.onKeyDown(props);
          },

          onExit() {
            component.destroy();
            component.element.remove();

            if (resizeObserver) {
              resizeObserver.disconnect();
            }
            if (windowEventListener) {
              window.removeEventListener('resize', windowEventListener);
              window.removeEventListener(
                'orientationchange',
                windowEventListener
              );
            }
            if (intersectionObserver) {
              intersectionObserver.disconnect();
            }
          },
        };
      },
    };
  }, [refinedSuggestions]);
}
